import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import ReferenceList from '../../components/health/ReferenceList';
import foodTopics from '../../data/health/foodTopics';

function formatCitations(text) {
  if (!text) return '';
  return text
    .replace(/\[(\d+)\]/g, '<a href="#ref-$1" class="health-cite">[$1]</a>')
    .replace(/\[VERIFY\]/g, '<span class="health-verify">[VERIFY]</span>');
}

const HealthFoodTopic = () => {
  const { slug } = useParams();
  const topic = foodTopics.find((t) => t.slug === slug);

  if (!topic) {
    return <Navigate to="/health/food" replace />;
  }

  const relatedTopicObjects = (topic.relatedTopics || [])
    .map((rs) => foodTopics.find((t) => t.slug === rs))
    .filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{topic.title} | Food & Nutrients</title>
        <meta name="description" content={topic.whatItIs} />
        <meta property="og:title" content={`${topic.title} — Food & Nutrients`} />
        <meta property="og:description" content={topic.whatItIs} />
      </Helmet>

      <motion.div
        className="health-page health-content"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Breadcrumb */}
        <div style={{ marginBottom: '1.5rem', fontSize: '0.8125rem' }}>
          <Link to="/health" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>Health</Link>
          <span style={{ color: 'var(--color-text-muted)', margin: '0 0.5rem' }}>/</span>
          <Link to="/health/food" style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>Food & Nutrients</Link>
          <span style={{ color: 'var(--color-text-muted)', margin: '0 0.5rem' }}>/</span>
          <span style={{ color: 'var(--color-accent)' }}>{topic.title}</span>
        </div>

        <h1 className="health-page-title">{topic.title}</h1>

        {/* Synonym tags */}
        {topic.synonyms && topic.synonyms.length > 0 && (
          <div className="health-topic-meta">
            {topic.synonyms.map((syn) => (
              <span key={syn} className="health-topic-tag">{syn}</span>
            ))}
          </div>
        )}

        {/* What it is */}
        <h2>What it is</h2>
        <p dangerouslySetInnerHTML={{ __html: formatCitations(topic.whatItIs) }} />

        {/* Why it matters */}
        <h2>Why it matters</h2>
        <p dangerouslySetInnerHTML={{ __html: formatCitations(topic.whyItMatters) }} />

        {/* Good sources */}
        {topic.sources && topic.sources.length > 0 && (
          <>
            <h2>Good sources</h2>
            <ul>
              {topic.sources.map((source, i) => (
                <li key={i}>{source}</li>
              ))}
            </ul>
          </>
        )}

        {/* How much */}
        <h2>How much you need</h2>
        <p dangerouslySetInnerHTML={{ __html: formatCitations(topic.howMuch) }} />

        {/* Deficiency / excess */}
        {topic.deficiencyExcess && (
          <>
            <h2>Signs of deficiency / excess</h2>
            <p dangerouslySetInnerHTML={{ __html: formatCitations(topic.deficiencyExcess) }} />
          </>
        )}

        {/* Age notes */}
        {topic.ageNotes && (
          <div className="health-callout" style={{ marginTop: '1rem' }}>
            <div className="health-callout-title">Age-specific notes</div>
            <div className="health-callout-text" dangerouslySetInnerHTML={{ __html: formatCitations(topic.ageNotes) }} />
          </div>
        )}

        {/* Myths */}
        {topic.myths && topic.myths.length > 0 && (
          <>
            <h2>Common myths</h2>
            {topic.myths.map((m, i) => (
              <div key={i} className="health-callout warning" style={{ marginBottom: '0.75rem' }}>
                <div className="health-callout-title">Myth: {m.myth}</div>
                <div className="health-callout-text" dangerouslySetInnerHTML={{ __html: formatCitations(m.fact) }} />
              </div>
            ))}
          </>
        )}

        {/* Related topics */}
        {relatedTopicObjects.length > 0 && (
          <div className="health-related">
            <div className="health-related-title">Related Topics</div>
            <div className="health-chips-grid">
              {relatedTopicObjects.map((rt) => (
                <Link key={rt.slug} to={`/health/food/${rt.slug}`} className="health-chip">
                  {rt.title}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* References */}
        <ReferenceList references={topic.references} />
      </motion.div>
    </>
  );
};

export default HealthFoodTopic;
